import React, { useEffect, useState } from 'react';
import './NovaRegraModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import OrangeSpinner from '../../../components/loading/OrangeSpinner';

type TabRegra = 'vitalicio' | 'bonificacoes' | 'descontos' | 'personalizada';

interface NovaRegraModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Grava a regra. O modal AGUARDA a promessa: enquanto ela não resolve, o botão fica em
   * "Salvando..." e só fecha no sucesso. Quem rejeita mantém o modal aberto com os dados
   * preenchidos — o hospedeiro já avisou o motivo pelo toast.
   */
  onSave: (regra: NovaRegra & { id?: string }) => void | Promise<void>;
  // Aba de regras atual (modelo_regra)
  activeTab: TabRegra;
  // Dados iniciais para modo edição
  initialData?: Partial<NovaRegra> & { id?: string };
  mode?: 'create' | 'edit';
  /**
   * De quem é a regra. Só a COOPERATIVA tem líder e equipe, então é o único tipo em que
   * "Calcular sobre" significa alguma coisa — nos demais o backend ignora o campo, e exibi-lo
   * faria o usuário acreditar ter restringido algo.
   */
  qualificadoTipo?: 'COOPERATIVA' | 'VOLUNTARIO' | 'REGIONAL';
}

interface NovaRegra {
  modelo: TabRegra;
  observacoes: string;
  contratoInicio?: string;
  contratoFim?: string | null;
  tipoRegra: 'PERCENTO' | 'VALOR';
  valor: number;
  meta?: number;
  validadeInicio: string;
  validadeFim: string; // pode ser vazio para representar nulo
  // Campos exclusivos para PERSONALIZADA
  escopo?: 'LIDER' | 'EQUIPE' | 'AMBOS';
  alvoDebito?: 'PAI' | 'FILHO';
  fonteCalculo?: 'PAI' | 'FILHO' | 'AMBOS';
  operacao?: 'CREDITO' | 'DEBITO';
  multiplicador?: 'POR_BOLETO' | 'FIXO_MENSAL';
}

const toDateInput = (iso?: string | null) => {
  if (!iso) return '';
  const d = iso.substring(0, 10); // YYYY-MM-DD
  return d;
};

const NovaRegraModal: React.FC<NovaRegraModalProps> = ({ isOpen, onClose, onSave, activeTab, initialData, mode = 'create', qualificadoTipo = 'COOPERATIVA' }) => {
  const [formData, setFormData] = useState<NovaRegra>({
    modelo: activeTab,
    observacoes: initialData?.observacoes ?? '',
    contratoInicio: toDateInput(initialData?.contratoInicio) ?? '',
    contratoFim: toDateInput(initialData?.contratoFim) ?? '',
    tipoRegra: (initialData?.tipoRegra as 'PERCENTO' | 'VALOR') ?? 'VALOR',
    valor: initialData?.valor ?? 0,
    meta: initialData?.meta ?? 0,
    validadeInicio: toDateInput(initialData?.validadeInicio) ?? '',
    validadeFim: toDateInput(initialData?.validadeFim) ?? '',
    escopo: initialData?.escopo ?? 'AMBOS',
    alvoDebito: initialData?.alvoDebito ?? 'PAI',
    fonteCalculo: initialData?.fonteCalculo ?? 'AMBOS',
    operacao: initialData?.operacao ?? 'CREDITO',
    multiplicador: initialData?.multiplicador ?? 'POR_BOLETO',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  // Salvamento em curso: trava o botão (segundo clique criaria a regra duas vezes) e mostra
  // o indicador — sem ele, o clique em "Salvar" não dava sinal nenhum até a lista recarregar.
  const [salvando, setSalvando] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? parseFloat(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.observacoes.trim()) {
      newErrors.observacoes = 'Observações é obrigatório';
    }

    if (formData.valor <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.validadeInicio) {
      newErrors.validadeInicio = 'Validade início é obrigatória';
    }

    // Data final é opcional; se informada, deve ser posterior ao início
    if (formData.validadeInicio && formData.validadeFim) {
      if (new Date(formData.validadeInicio) >= new Date(formData.validadeFim)) {
        newErrors.validadeFim = 'Validade fim deve ser posterior à validade início';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Atualiza o formulário quando abrir o modal ou quando os dados iniciais mudarem
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      modelo: activeTab,
      observacoes: initialData?.observacoes ?? '',
      contratoInicio: toDateInput(initialData?.contratoInicio) ?? '',
      contratoFim: toDateInput(initialData?.contratoFim) ?? '',
      tipoRegra: (initialData?.tipoRegra as 'PERCENTO' | 'VALOR') ?? 'VALOR',
      valor: initialData?.valor ?? 0,
      meta: initialData?.meta ?? 0,
      validadeInicio: toDateInput(initialData?.validadeInicio) ?? '',
      validadeFim: toDateInput(initialData?.validadeFim) ?? '',
      escopo: initialData?.escopo ?? 'AMBOS',
      alvoDebito: initialData?.alvoDebito ?? 'PAI',
      fonteCalculo: initialData?.fonteCalculo ?? 'AMBOS',
    });
    setErrors({});
  }, [isOpen, initialData, activeTab, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (salvando || !validateForm()) return;

    setSalvando(true);
    try {
      await onSave({
        ...formData,
        modelo: activeTab, // Garantir que o modelo seja sempre o da aba ativa
        id: initialData?.id
      });
      handleClose();
    } catch {
      // O hospedeiro já avisou o motivo (notify). O modal fica aberto com o que foi digitado,
      // para o usuário corrigir ou tentar de novo em vez de preencher tudo outra vez.
    } finally {
      setSalvando(false);
    }
  };

  const handleClose = () => {
    setFormData({
      modelo: activeTab,
      observacoes: '',
      contratoInicio: '',
      contratoFim: '',
      tipoRegra: 'VALOR',
      valor: 0,
      meta: 0,
      validadeInicio: '',
      validadeFim: '',
      escopo: 'AMBOS',
      alvoDebito: 'PAI',
      fonteCalculo: 'AMBOS',
      operacao: 'CREDITO',
      multiplicador: 'POR_BOLETO',
    });
    setErrors({});
    onClose();
  };

  const getTipoLabel = () => {
    const labels: Record<TabRegra, string> = {
      vitalicio: 'Vitalício',
      bonificacoes: 'Bonificações',
      descontos: 'Descontos',
      personalizada: 'Personalizada',
    };
    return labels[activeTab];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content nova-regra-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'edit' ? 'Editar Regra' : 'Nova Regra'} - {getTipoLabel()}</h2>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Observações - 3 linhas */}
          <div className="form-row">
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="observacoes">Observações *</label>
              <textarea
                id="observacoes"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleInputChange}
                placeholder="Descreva a regra de comissionamento"
                rows={3}
                className={errors.observacoes ? 'error' : ''}
              />
              {errors.observacoes && <span className="error-message">{errors.observacoes}</span>}
            </div>
          </div>

          {/* Contrato início | Contrato fim */}
          <div className="form-row">
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="contratoInicio">Contrato Início</label>
              <input
                type="date"
                id="contratoInicio"
                name="contratoInicio"
                value={formData.contratoInicio || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="contratoFim">Contrato Fim</label>
              <input
                type="date"
                id="contratoFim"
                name="contratoFim"
                value={formData.contratoFim || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Tipo de regra (Percento/Valor) | Valor | Meta */}
          <div className="form-row">
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="tipoRegra">Tipo de Regra</label>
              <select id="tipoRegra" name="tipoRegra" value={formData.tipoRegra} onChange={handleInputChange}>
                <option value="PERCENTO">Percento</option>
                <option value="VALOR">Valor</option>
              </select>
            </div>
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="valor">Valor *</label>
              <input
                type="number"
                id="valor"
                name="valor"
                value={formData.valor}
                onChange={handleInputChange}
                placeholder="0,00"
                step="0.01"
                min="0"
                className={errors.valor ? 'error' : ''}
              />
              {errors.valor && <span className="error-message">{errors.valor}</span>}
            </div>
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="meta">Meta</label>
              <input
                type="number"
                id="meta"
                name="meta"
                value={formData.meta ?? 0}
                onChange={handleInputChange}
                step="1"
                min="0"
              />
            </div>
          </div>

          {/* Campos exclusivos da aba Personalizada */}
          {activeTab === 'personalizada' && (
            <>
              <div className="form-row">
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label htmlFor="operacao">Operação</label>
                  <select id="operacao" name="operacao" value={formData.operacao ?? 'CREDITO'} onChange={handleInputChange}>
                    <option value="CREDITO">Crédito (soma no repasse)</option>
                    <option value="DEBITO">Débito (subtrai do repasse)</option>
                  </select>
                </div>
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label htmlFor="multiplicador">Cálculo do valor</label>
                  <select id="multiplicador" name="multiplicador" value={formData.multiplicador ?? 'POR_BOLETO'} onChange={handleInputChange}>
                    <option value="POR_BOLETO">Valor x quantidade de boletos</option>
                    <option value="FIXO_MENSAL">Fixo mensal (não multiplica)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label htmlFor="escopo">Aplica para</label>
                  <select id="escopo" name="escopo" value={formData.escopo ?? 'AMBOS'} onChange={handleInputChange}>
                    <option value="LIDER">Somente Líder (Cooperativa)</option>
                    <option value="EQUIPE">Somente Equipe (Voluntários)</option>
                    <option value="AMBOS">Líder e Equipe</option>
                  </select>
                </div>
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label htmlFor="alvoDebito">Creditar em</label>
                  <select id="alvoDebito" name="alvoDebito" value={formData.alvoDebito ?? 'PAI'} onChange={handleInputChange}>
                    <option value="PAI">Cooperativa (pai)</option>
                    <option value="FILHO">Voluntário (filho)</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/*
            "Calcular sobre" vale para TODA regra de cooperativa, não só a Personalizada (at-03).
            Até aqui ele vivia dentro do bloco acima, e por isso as regras "somente líder = R$10" e
            "somente Voluntários dele = R$5" do Vitalício nasciam sem a distinção — as duas
            contavam o mesmo conjunto (391 boletos cada em 07/2026), e só dava para corrigir por SQL.
          */}
          {qualificadoTipo === 'COOPERATIVA' && (
            <div className="form-row">
              <div className="form-group" style={{ textAlign: 'left' }}>
                <label htmlFor="fonteCalculo">Calcular sobre</label>
                <select id="fonteCalculo" name="fonteCalculo" value={formData.fonteCalculo ?? 'AMBOS'} onChange={handleInputChange}>
                  <option value="AMBOS">Todos os boletos (líder e equipe)</option>
                  <option value="PAI">Somente boletos do líder</option>
                  <option value="FILHO">Somente boletos da equipe</option>
                </select>
              </div>
            </div>
          )}

          {/* Validade início | Validade fim */}
          <div className="form-row">
            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="validadeInicio">Validade Início *</label>
              <input
                type="date"
                id="validadeInicio"
                name="validadeInicio"
                value={formData.validadeInicio}
                onChange={handleInputChange}
                className={errors.validadeInicio ? 'error' : ''}
              />
              {errors.validadeInicio && <span className="error-message">{errors.validadeInicio}</span>}
            </div>

            <div className="form-group" style={{ textAlign: 'left' }}>
              <label htmlFor="validadeFim">Validade Fim</label>
              <input
                type="date"
                id="validadeFim"
                name="validadeFim"
                value={formData.validadeFim}
                onChange={handleInputChange}
                className={errors.validadeFim ? 'error' : ''}
              />
              {errors.validadeFim && <span className="error-message">{errors.validadeFim}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={salvando}>
              Cancelar
            </button>
            <button type="submit" className="btn-save" disabled={salvando} aria-busy={salvando}>
              {salvando ? (
                <>
                  <OrangeSpinner inline pequeno rotulo="Salvando" />
                  Salvando...
                </>
              ) : (
                mode === 'edit' ? 'Salvar Alterações' : 'Salvar Regra'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaRegraModal;